/**
 * OAuth em janela popup: a aba principal nao perde o contexto.
 * Usa postMessage + BroadcastChannel (fallback quando o opener some apos o redirect).
 */

export const TIPO_MENSAGEM_OAUTH_POPUP = "renke-oauth-resultado" as const;
export const CANAL_OAUTH_POPUP = "renke-oauth-popup";

export type ResultadoOauthPopup = {
  readonly oauth: string;
};

type MensagemOauthPopup = {
  readonly tipo: typeof TIPO_MENSAGEM_OAUTH_POPUP;
  readonly oauth: string;
};

export function lerOauthDoHash(hash = window.location.hash): string | null {
  const query = hash.split("?")[1] ?? "";
  return new URLSearchParams(query).get("oauth");
}

function limparOauthDoHash(hash: string): void {
  const params = new URLSearchParams(hash.split("?")[1] ?? "");
  params.delete("oauth");
  const resto = params.toString();
  const novoHash = resto
    ? `configuracoes?${resto}`
    : "configuracoes?secao=meta";
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}#${novoHash}`,
  );
}

function montarMensagem(oauth: string): MensagemOauthPopup {
  return { tipo: TIPO_MENSAGEM_OAUTH_POPUP, oauth };
}

function ehMensagemOauth(dados: unknown): dados is MensagemOauthPopup {
  if (!dados || typeof dados !== "object") return false;
  const msg = dados as Partial<MensagemOauthPopup>;
  return (
    msg.tipo === TIPO_MENSAGEM_OAUTH_POPUP &&
    typeof msg.oauth === "string" &&
    msg.oauth.length > 0
  );
}

/** No popup apos o redirect: avisa a janela pai e tenta fechar. */
export function notificarOpenerEFecharSePopupOauth(
  hash = window.location.hash,
): string | null {
  const oauth = lerOauthDoHash(hash);
  if (!oauth) return null;

  const veioDePopup = Boolean(window.opener) || window.name === "renke-oauth";
  if (!veioDePopup) return null;

  const mensagem = montarMensagem(oauth);

  try {
    const canal = new BroadcastChannel(CANAL_OAUTH_POPUP);
    canal.postMessage(mensagem);
    canal.close();
  } catch {
    // BroadcastChannel pode falhar em contextos raros; postMessage segue.
  }

  if (window.opener && !window.opener.closed) {
    try {
      window.opener.postMessage(mensagem, window.location.origin);
    } catch {
      // COOP / opener inacessivel
    }
  }

  limparOauthDoHash(hash);
  window.setTimeout(() => {
    window.close();
  }, 50);
  return oauth;
}

/**
 * Abre o OAuth numa popup centrada.
 * Resolve com o resultado, `null` se o usuario cancelou, ou faz fallback
 * para navegacao na mesma aba se o browser bloquear a popup.
 */
export function abrirOauthEmPopup(
  url: string,
): Promise<ResultadoOauthPopup | null> {
  const largura = 640;
  const altura = 740;
  const esquerda = Math.max(
    0,
    Math.round(window.screenX + (window.outerWidth - largura) / 2),
  );
  const topo = Math.max(
    0,
    Math.round(window.screenY + (window.outerHeight - altura) / 2),
  );

  const popup = window.open(
    url,
    "renke-oauth",
    [
      `width=${largura}`,
      `height=${altura}`,
      `left=${esquerda}`,
      `top=${topo}`,
      "popup=yes",
      "resizable=yes",
      "scrollbars=yes",
    ].join(","),
  );

  if (!popup) {
    window.location.assign(url);
    return Promise.resolve(null);
  }

  const janela = popup;
  janela.focus();

  return new Promise((resolve) => {
    let finalizado = false;
    let graceTimer: number | null = null;
    let canal: BroadcastChannel | null = null;

    function concluir(resultado: ResultadoOauthPopup | null) {
      if (finalizado) return;
      finalizado = true;
      window.removeEventListener("message", aoMensagem);
      window.clearInterval(pollFechamento);
      if (graceTimer != null) window.clearTimeout(graceTimer);
      try {
        canal?.close();
      } catch {
        // ignore
      }
      resolve(resultado);
    }

    function aoSucesso(oauth: string) {
      if (!janela.closed) {
        try {
          janela.close();
        } catch {
          // ignore
        }
      }
      concluir({ oauth });
    }

    function aoMensagem(evento: MessageEvent) {
      if (evento.origin !== window.location.origin) return;
      if (!ehMensagemOauth(evento.data)) return;
      aoSucesso(evento.data.oauth);
    }

    function aoCanal(evento: MessageEvent) {
      if (!ehMensagemOauth(evento.data)) return;
      aoSucesso(evento.data.oauth);
    }

    try {
      canal = new BroadcastChannel(CANAL_OAUTH_POPUP);
      canal.addEventListener("message", aoCanal);
    } catch {
      canal = null;
    }

    const pollFechamento = window.setInterval(() => {
      if (!janela.closed) return;
      // Espera o postMessage/BroadcastChannel chegar antes de assumir cancelamento.
      if (graceTimer != null) return;
      graceTimer = window.setTimeout(() => {
        concluir(null);
      }, 800);
    }, 300);

    window.addEventListener("message", aoMensagem);
  });
}
